import React, { useState } from 'react';
import { Button, Space, Table, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PreviewModal from './Preview';

interface Contract {
  id: string;
  contractId: string;
  platform: string;
  amount: number;
  period: number;
  phoneNumber: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  email: string;
  createdAt: string;
  link?: string;
}

interface ContractsTableProps {
  data?: Contract[];
  isLoading: boolean;
  refetch: () => void;
}

const ContractsTable: React.FC<ContractsTableProps> = ({ data = [], isLoading, refetch }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const statusLabels: Record<string, string> = {
    accepted: 'Activate',
    rejected: 'Reject',
    pending: 'Pending',
    expired: 'Expire',
  };

  const handlePreview = (id: string) => {
    setSelectedId(id);
    setOpen(true);
  };

  const hasNonPending = data.some((item) => item.status !== 'pending');

  const columns = [
    {
      title: 'Contract ID',
      dataIndex: 'contractId',
      key: 'contractId',
      render: (_: any, record: any) => `#${record.contractId}`,
    },
    {
      title: 'Contracting Company',
      dataIndex: ['ownerCompany', 'name'],
      key: 'Contracting',
      render: (_: any, record: any) => record.ownerCompany?.name || '-',
    },
    { title: 'Platform', dataIndex: 'platform', key: 'platform' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount' },
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      render: (text: number) => `${text} months`,
    },
    { title: 'Phone', dataIndex: 'phoneNumber', key: 'phoneNumber' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'blue';

        if (status === 'pending') color = '#D7D8E0';
        if (status === 'accepted') color = '#27AE60';
        if (status === 'rejected') color = '#F64747';
        if (status === 'expired') color = '#F99E2C';

        const statusLabels: Record<string, string> = {
          pending: 'Pending',
          accepted: 'Activated',
          rejected: 'Rejected',
          expired: 'Expired',
        };

        return (
          <Tag style={{ textTransform: 'capitalize' }} color={color}>
            {statusLabels[status] || status}
          </Tag>
        );
      },
    },
    ...(hasNonPending
      ? [
          {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (text: string | null) => (text ? dayjs(text).format('MMM DD, YYYY') : '-'),
          },
          {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (text: string | null) => (text ? dayjs(text).format('MMM DD, YYYY') : '-'),
          },
        ]
      : []),
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string | null) => dayjs(text).format('MMM DD, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Contract) => {
        return (
          <Space size="small">
            <Button type="default" icon={<EyeOutlined />} onClick={() => handlePreview(record.id)}>
              Preview
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={false}
      />

      <PreviewModal id={selectedId} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default ContractsTable;
